import { useState } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Badge, Alert } from 'react-bootstrap';
import { FaArrowLeft, FaUser, FaExchangeAlt, FaMapMarkerAlt, FaCalendarAlt, FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../services/api';
import { toast } from 'react-toastify';
import './InscripcionManagement.css';

const CambioDeSede = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const torneoNombreParam = searchParams.get('torneoNombre') || '';
  const torneoIdParam = searchParams.get('torneoId') || '';

  // Estados
  const [searchTerm, setSearchTerm] = useState('');
  const [futbolistas, setFutbolistas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFutbolista, setSelectedFutbolista] = useState(null);
  const [inscripcionInfo, setInscripcionInfo] = useState(null);
  const [jornadasFuturas, setJornadasFuturas] = useState([]);
  const [loadingJornadas, setLoadingJornadas] = useState(false);
  const [selectedJornadaOrigen, setSelectedJornadaOrigen] = useState(null);
  const [jornadasDestino, setJornadasDestino] = useState([]);
  const [loadingDestino, setLoadingDestino] = useState(false);
  const [selectedJornadaDestino, setSelectedJornadaDestino] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Buscar futbolistas con jornadas futuras en el torneo
  const handleSearch = async (term) => {
    setSearchTerm(term);
    if (term.length < 4) { setFutbolistas([]); return; }
    if (!torneoIdParam) { toast.error('No se ha especificado el torneo'); return; }

    setLoading(true);
    try {
      const response = await API.get(`/admin/cambio-sede/buscar-futbolistas`, {
        params: { torneoId: torneoIdParam, search: term }
      });
      const result = response.data;
      setFutbolistas(result.success ? result.data : []);
    } catch (error) {
      console.error('Error buscando futbolistas:', error);
      toast.error('Error al buscar futbolistas');
    } finally {
      setLoading(false);
    }
  };

  // Seleccionar futbolista → cargar jornadas futuras con sede
  const handleSelectFutbolista = async (futbolista) => {
    setSelectedFutbolista(futbolista);
    setSelectedJornadaOrigen(null);
    setJornadasDestino([]);
    setSelectedJornadaDestino(null);
    setLoadingJornadas(true);

    try {
      const response = await API.get(`/admin/cambio-sede/${futbolista.id}/jornadas-futuras`, {
        params: { torneoId: torneoIdParam }
      });
      const result = response.data;
      if (result.success) {
        setInscripcionInfo(result.data.inscripcion);
        setJornadasFuturas(result.data.jornadas);
      } else {
        toast.error(result.message || 'Error al cargar jornadas');
        setSelectedFutbolista(null);
      }
    } catch (error) {
      console.error('Error cargando jornadas:', error);
      toast.error('Error al cargar información');
      setSelectedFutbolista(null);
    } finally {
      setLoadingJornadas(false);
    }
  };

  // Seleccionar jornada origen → cargar jornadas destino en otras sedes
  const handleSelectJornadaOrigen = async (jornada) => {
    setSelectedJornadaOrigen(jornada);
    setSelectedJornadaDestino(null);
    setLoadingDestino(true);

    try {
      const response = await API.get(
        `/admin/cambio-sede/${selectedFutbolista.id}/jornadas-destino`,
        { params: { torneoId: torneoIdParam, jornadaOrigenId: jornada.jornada_id } }
      );
      const result = response.data;
      setJornadasDestino(result.success ? result.data : []);
    } catch (error) {
      console.error('Error cargando destinos:', error);
      toast.error('Error al cargar sedes destino');
    } finally {
      setLoadingDestino(false);
    }
  };

  // Ejecutar el cambio de sede
  const handleEjecutarCambio = async () => {
    if (!selectedFutbolista || !selectedJornadaOrigen || !selectedJornadaDestino) return;
    setProcessing(true);

    try {
      const response = await API.post(`/admin/cambio-sede/${selectedFutbolista.id}/ejecutar`, {
        torneoId: parseInt(torneoIdParam),
        jornadaOrigenId: selectedJornadaOrigen.jornada_id,
        jornadaDestinoId: selectedJornadaDestino.jornada_id
      });
      const result = response.data;
      if (result.success) {
        toast.success(`✅ ${result.message}`);
        // Limpiar todo
        setSelectedFutbolista(null);
        setSelectedJornadaOrigen(null);
        setSelectedJornadaDestino(null);
        setJornadasFuturas([]);
        setJornadasDestino([]);
        setInscripcionInfo(null);
        setSearchTerm('');
        setFutbolistas([]);
      } else {
        toast.error(result.message || 'Error al cambiar sede');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setProcessing(false);
    }
  };

  // Limpiar selección
  const handleClearSelection = () => {
    setSelectedFutbolista(null);
    setSelectedJornadaOrigen(null);
    setSelectedJornadaDestino(null);
    setJornadasFuturas([]);
    setJornadasDestino([]);
    setInscripcionInfo(null);
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    return new Date(fecha).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  return (
    <AdminLayout
      title={`Cambio de Sede${torneoNombreParam ? ` — ${torneoNombreParam}` : ''}`}
      subtitle="Mover futbolista a una jornada en otra sede del mismo torneo"
    >
      <Container fluid className="inscripcion-management inscripcion-white-bg p-0">
        {/* Navegación */}
        <Row className="mb-3">
          <Col>
            <Button variant="outline-secondary" onClick={() => navigate('/admin/matches')}>
              <FaArrowLeft className="me-2" />Volver a Torneos
            </Button>
          </Col>
        </Row>

        {/* Búsqueda */}
        <Row className="mb-4">
          <Col xs={12}>
            <Form.Control
              type="text"
              placeholder="🔍 Buscar futbolista por nombre..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              disabled={loading || selectedFutbolista}
            />
            <small className="text-muted">💡 Escribe al menos 4 caracteres para buscar</small>
          </Col>
        </Row>

        {loading && (
          <Row className="mb-4"><Col className="text-center"><Spinner animation="border" /><p className="mt-2">Buscando futbolistas...</p></Col></Row>
        )}

        {/* Resultados búsqueda */}
        {!selectedFutbolista && futbolistas.length > 0 && (
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header><h5>Resultados ({futbolistas.length})</h5></Card.Header>
                <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {futbolistas.map((f) => (
                    <Card key={f.id} className="mb-2" style={{ cursor: 'pointer' }} onClick={() => handleSelectFutbolista(f)}>
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <FaUser className="me-2" />
                            <strong>{f.nombre}</strong>
                            <Badge bg="success" className="ms-2">Saldo: {f.saldo_jornadas}</Badge>
                          </div>
                          <Badge bg="primary">Seleccionar</Badge>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Futbolista seleccionado */}
        {selectedFutbolista && (
          <>
            <Row className="mb-3">
              <Col>
                <Card className="border-primary">
                  <Card.Body className="py-2 d-flex justify-content-between align-items-center">
                    <div>
                      <FaUser className="me-2 text-primary" />
                      <strong>{selectedFutbolista.nombre}</strong>
                      {inscripcionInfo && (
                        <Badge bg="success" className="ms-2">Saldo: {inscripcionInfo.saldo_jornadas}</Badge>
                      )}
                    </div>
                    <Button variant="outline-secondary" size="sm" onClick={handleClearSelection}>Cambiar</Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {loadingJornadas ? (
              <Row className="mb-3"><Col className="text-center"><Spinner animation="border" size="sm" className="me-2" />Cargando jornadas...</Col></Row>
            ) : (
              <>
              <Row className="mb-3">
                {/* Columna: Jornada Origen */}
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Header className="bg-warning bg-opacity-25 py-2">
                      <h6 className="mb-0"><FaCalendarAlt className="me-1" />Jornada Origen</h6>
                    </Card.Header>
                    <Card.Body className="p-3">
                      {jornadasFuturas.length === 0 ? (
                        <Alert variant="info" className="small py-2 mb-0">No tiene jornadas futuras en este torneo.</Alert>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '8px' }}>
                          {jornadasFuturas.map((j) => (
                            <div
                              key={j.futbolista_en_jornada_id}
                              onClick={() => handleSelectJornadaOrigen(j)}
                              style={{
                                cursor: 'pointer',
                                padding: '8px 10px',
                                borderRadius: '8px',
                                border: selectedJornadaOrigen?.jornada_id === j.jornada_id ? '2px solid #e0a800' : '1px solid #dee2e6',
                                backgroundColor: selectedJornadaOrigen?.jornada_id === j.jornada_id ? '#fff8e1' : '#f8f9fa',
                                transition: 'all 0.15s ease',
                                textAlign: 'center',
                                fontSize: '12px',
                              }}
                            >
                              <div className="fw-bold" style={{ fontSize: '13px' }}>J{j.numero}</div>
                              <div className="text-muted" style={{ fontSize: '10px' }}>{formatFecha(j.fecha)}</div>
                              <div className="text-muted" style={{ fontSize: '10px' }}><FaMapMarkerAlt size={8} className="me-1" />{j.sede_nombre}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                {/* Columna: Jornada Destino */}
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Header className="bg-success bg-opacity-25 py-2">
                      <h6 className="mb-0"><FaMapMarkerAlt className="me-1" />Jornada Destino</h6>
                    </Card.Header>
                    <Card.Body className="p-3">
                      {!selectedJornadaOrigen ? (
                        <Alert variant="secondary" className="small py-2 mb-0">Selecciona una jornada origen primero.</Alert>
                      ) : loadingDestino ? (
                        <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                      ) : jornadasDestino.length === 0 ? (
                        <Alert variant="warning" className="small py-2 mb-0">No hay jornadas disponibles en otras sedes.</Alert>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '8px' }}>
                          {jornadasDestino.map((j) => (
                            <div
                              key={j.jornada_id}
                              onClick={() => setSelectedJornadaDestino(j)}
                              style={{
                                cursor: 'pointer',
                                padding: '8px 10px',
                                borderRadius: '8px',
                                border: selectedJornadaDestino?.jornada_id === j.jornada_id ? '2px solid #198754' : '1px solid #dee2e6',
                                backgroundColor: selectedJornadaDestino?.jornada_id === j.jornada_id ? '#e8f5e9' : '#f8f9fa',
                                transition: 'all 0.15s ease',
                                textAlign: 'center',
                                fontSize: '12px',
                              }}
                            >
                              <div className="fw-bold" style={{ fontSize: '13px' }}>J{j.numero}</div>
                              <div className="text-muted" style={{ fontSize: '10px' }}>{formatFecha(j.fecha)}</div>
                              <div className="text-muted" style={{ fontSize: '10px' }}><FaMapMarkerAlt size={8} className="me-1" />{j.sede_nombre}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Resumen y confirmar */}
              {selectedJornadaOrigen && selectedJornadaDestino ? (
                <Row className="mb-3">
                  <Col md={6} className="mx-auto">
                    <Card className="border-primary">
                      <Card.Header className="bg-primary bg-opacity-25 py-2">
                        <h6 className="mb-0"><FaExchangeAlt className="me-1" />Resumen del Cambio</h6>
                      </Card.Header>
                      <Card.Body className="p-3">
                        <div className="d-flex align-items-center justify-content-center gap-3" style={{ fontSize: '13px' }}>
                          <div className="text-center">
                            <div className="text-muted small">Origen</div>
                            <div className="fw-bold">J{selectedJornadaOrigen.numero} — {selectedJornadaOrigen.sede_nombre}</div>
                            <div className="small text-muted">{formatFecha(selectedJornadaOrigen.fecha)}</div>
                          </div>
                          <FaArrowRight className="text-success" size={20} />
                          <div className="text-center">
                            <div className="text-muted small">Destino</div>
                            <div className="fw-bold text-success">J{selectedJornadaDestino.numero} — {selectedJornadaDestino.sede_nombre}</div>
                            <div className="small text-muted">{formatFecha(selectedJornadaDestino.fecha)}</div>
                          </div>
                        </div>
                        <div className="text-center mt-3">
                          <Button
                            variant="success"
                            onClick={handleEjecutarCambio}
                            disabled={processing}
                          >
                            {processing ? <Spinner animation="border" size="sm" /> : <><FaCheckCircle className="me-1" />Confirmar Cambio</>}
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              ) : (
                <Row className="mb-3">
                  <Col md={6} className="mx-auto">
                    <Alert variant="secondary" className="small text-center py-2 mb-0">
                      Selecciona jornada origen y destino para ver el resumen.
                    </Alert>
                  </Col>
                </Row>
              )}
              </>
            )}
          </>
        )}
      </Container>
    </AdminLayout>
  );
};

export default CambioDeSede;
